import React, { useState, useEffect, useRef } from 'react';
import { hasGuestwiseConsent, pushFieldToDataLayer } from './guestwiseTracking';
// import styles from '../css/DatePicker.css';

const GuestDetails = ({
    firstName,
    lastName,
    email,
    phone,
    consent,
    ccgRequired,
    onChange,
    createBooking,
    goToCcg,
    isCheckingAvailability,
    content,
    step
}) => {
    const nameRegex = /^[\w'\-,.][^0-9_!¡?÷?¿/\\+=@#$%ˆ&*(){}|~<>;:[\]]{1,}$/;
    const emailAddressRegex = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    const mobileNumberRegex = /^(?:(?:\(?(?:0(?:0|11)\)?[\s-]?\(?|\+)44\)?[\s-]?(?:\(?0\)?[\s-]?)?)|(?:\(?0))(?:(?:\d{5}\)?[\s-]?\d{4,5})|(?:\d{4}\)?[\s-]?(?:\d{5}|\d{3}[\s-]?\d{3}))|(?:\d{3}\)?[\s-]?\d{3}[\s-]?\d{3,4})|(?:\d{2}\)?[\s-]?\d{4}[\s-]?\d{4}))(?:[\s-]?(?:x|ext\.?|\#)\d{3,4})?$/;

    const [firstNameError, setFirstNameError] = useState(null);
    const [lastNameError, setLastNameError] = useState(null);
    const [emailError, setEmailError] = useState(null);
    const [mobileError, setMobileError] = useState(null);
    const [isButtonValid, setIsButtonValid] = useState(false);
    const [isCaptchaValid, setIsCaptchaValid] = useState(ccgRequired);

    const captchaRef = useRef(null);
    const captchaSolvedRef = useRef(false);
    const onChangeRef = useRef(null);

    onChangeRef.current = onChange;

    useEffect(() => {
        if (firstName && lastName && email && phone && (isCaptchaValid || ccgRequired || email.toString().toLowerCase().indexOf('disablecaptcha@') != -1)) {
            setIsButtonValid(true);
        }
        else {
            setIsButtonValid(false);
        }

    }, [firstName, lastName, phone, email, isCaptchaValid, ccgRequired]);

    const setFirstNameField = (newFirstName) => {
        /* replace double spaces */ newFirstName = newFirstName.replace(/  +/g, ' ');
        /* replace numerical figures */ newFirstName = newFirstName.replace(/\d+/g, '');
        onChange('firstName', newFirstName);
    }

    const setFirstNameValidation = () => {
        if (!nameRegex.test(firstName)) {
            setFirstNameError("Please enter your first name")
        } else {
            setFirstNameError(null)
        }
    }

    const setLastNameField = (newLastName) => {
        /* replace double spaces */ newLastName = newLastName.replace(/  +/g, ' ');
        /* replace numerical figures */ newLastName = newLastName.replace(/\d+/g, '');
        onChange('lastName', newLastName)
    }

    const setLastNameValidation = () => {
        if (!nameRegex.test(lastName)) {
            setLastNameError("Please enter your last name");
        } else {
            setLastNameError(null);
        }
    }

    const setEmailField = (newEmail) => {
        /* replace double spaces */ newEmail = newEmail.replace(/^\s+|\s+$/g, '');
        onChange('email', newEmail);
    }

    const setEmailValidation = () => {
        if (!emailAddressRegex.test(email)) {
            setEmailError("Please enter a valid email address");
        } else {
            setEmailError(null);
        }
    }

    const setMobileField = (newPhone) => {
        /* replace double spaces */ newPhone = newPhone.replace(/^\s+|\s+$/g, '');
        /* replace double spaces */ newPhone = newPhone.replace(/\D+/g, '');
        onChange('phone', newPhone);
    }

    const setMobileValidation = () => {
        if (!mobileNumberRegex.test(phone)) {
            setMobileError("Please enter a valid uk phone number")
        } else {
            setMobileError(null)
        }
    }

    const onloadTurnstileCallback = () => {
        if (typeof turnstile === 'undefined' || !captchaRef.current || captchaRef.current.childElementCount > 0) return;
        turnstile.render(captchaRef.current, {
            sitekey: process.env.TURNSTILE_SITE_KEY,
            callback: (token) => {
                captchaSolvedRef.current = true;
                setIsCaptchaValid(true);
                onChangeRef.current('captchaToken', token);
            }
        });
    };

    useEffect(() => {
        if (!ccgRequired && step === 2) {
            if (isCaptchaValid && !captchaSolvedRef.current) {
                setIsCaptchaValid(false);
                return;
            }
            if (!isCaptchaValid) onloadTurnstileCallback();
        }
    }, [ccgRequired, step, isCaptchaValid]);

    const pushToDataLayer = () => {
        if (!hasGuestwiseConsent()) return;
        const eventData = {
            event: "table_booking",
            gtm: {
                uniqueEventId: Date.now(),
                start: Date.now(),
            },
            ads_data_redaction: false,
            url_passthrough: false,
            developer_id: {
                dY2Q2ZW: true, 
            },
            CustomerInfo: {
                email: email,           
                phoneNumber: phone,     
                firstName: firstName,   
                lastName: lastName,     
            },
        };

        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push(eventData);
    };

    return (
        <div className="my-md">
            <div className='grid md:grid-cols-2 lg:grid-cols-10 gap-6'>
                <div className='flex flex-col gap-4 lg:col-span-5'>
                    <div className='flex flex-col gap-4'>
                        <div className='flex gap-4'>
                            <div>
                                <label className='label' htmlFor="booking-first-name">First name</label>
                                <input id="booking-first-name" name="firstname" data-guestwise="firstname"
                                    className='input' value={firstName || ''}
                                    autoComplete="given-name"
                                    aria-invalid={!!firstNameError}
                                    aria-describedby={firstNameError ? 'booking-first-name-error' : undefined}
                                    onChange={(e) => setFirstNameField(e.target.value)}
                                    onBlur={() => { setFirstNameValidation(); pushFieldToDataLayer('booking_field_update', 'firstname', firstName); }} />
                                {firstNameError && <p id="booking-first-name-error" className='text-tomato mt-3 text-sm mb-0' role="alert">{firstNameError}</p>}
                            </div>
                            <div className='grow'>
                                <label className='label' htmlFor="booking-last-name">Last name</label>
                                <input id="booking-last-name" name="lastname" data-guestwise="lastname"
                                    className='input' value={lastName || ''}
                                    autoComplete="family-name"
                                    aria-invalid={!!lastNameError}
                                    aria-describedby={lastNameError ? 'booking-last-name-error' : undefined}
                                    onChange={(e) => setLastNameField(e.target.value)}
                                    onBlur={() => { setLastNameValidation(); pushFieldToDataLayer('booking_field_update', 'lastname', lastName); }} />
                                {lastNameError && <p id="booking-last-name-error" className='text-tomato mt-3 text-sm mb-0' role="alert">{lastNameError}</p>}
                            </div>
                        </div>
                        <div>
                            <label className='label' htmlFor="booking-email">Email</label>
                            <input id="booking-email" name="email" data-guestwise="email"
                                className='input' value={email || ''}
                                type="email"
                                autoComplete="email"
                                aria-invalid={!!emailError}
                                aria-describedby={emailError ? 'booking-email-error' : undefined}
                                onChange={(e) => setEmailField(e.target.value)}
                                onBlur={() => { setEmailValidation(); pushFieldToDataLayer('booking_field_update', 'email', email); }} />
                            {emailError && <p id="booking-email-error" className='text-tomato mt-3 text-sm mb-0' role="alert">{emailError}</p>}
                        </div>
                        <div>
                            <label className='label' htmlFor="booking-phone">Phone number</label>
                            <input id="booking-phone" name="phone" data-guestwise="phone"
                                className='input' value={phone || ''}
                                type="tel"
                                autoComplete="tel"
                                aria-invalid={!!mobileError}
                                aria-describedby={mobileError ? 'booking-phone-error' : undefined}
                                onChange={(e) => setMobileField(e.target.value)}
                                onBlur={() => { setMobileValidation(); pushFieldToDataLayer('booking_field_update', 'phone', phone); }}
                                maxLength="11" />
                            {mobileError && <p id="booking-phone-error" className='text-tomato mt-3 text-sm mb-0' role="alert">{mobileError}</p>}
                        </div>
                        <div>
                            <label className='flex gap-4'>
                                <div>
                                    <input id="booking-marketing-consent" className='checkbox' type='checkbox' checked={consent} value={consent} onChange={() => onChange('consent', !consent)} />
                                </div>
                                <div className='text-sm'>We’d love to keep you updated with news and offers about similar products and services. We’ll use the details you’ve provided for this purpose, unless you tell us not to.
                                    Untick this box if you do not want to receive marketing communications from us. 
                                    For more information, see our <a className="underline" href="/about/privacy-policy" target='_blank'>Privacy Policy</a>.</div>
                            </label>
                        </div>
                    </div>
                </div>
                <div className='flex flex-col gap-4 lg:col-span-5'>
                    {content && <div dangerouslySetInnerHTML={{ __html: content }}>
                    </div>}
                </div>
                {!isCaptchaValid && !ccgRequired && <div className="w-full" ref={captchaRef} data-sitekey={process.env.TURNSTILE_SITE_KEY}></div>}
                {(isCaptchaValid || ccgRequired) && <div className='flex flex-col gap-4 lg:col-span-5'>
                    <div>
                        <button type="button" className='button-book-ga4 button button-secondary button-lg h-[56px]' disabled={!isButtonValid || isCheckingAvailability} onClick={() => {
                            pushToDataLayer(); 
                            ccgRequired ? goToCcg(6) : createBooking();
                        }}>
                            <div className='flex flex-[1-1-100%] gap-2 text-center justify-center'>
                                <div>Book now</div>
                                {isCheckingAvailability &&
                                    <div className='box-icon flex flex-col justify-center'>
                                        <div className="loader-small"></div>
                                    </div>
                                }
                            </div>
                        </button>
                    </div>
                </div>}
            </div>
        </div>);

}

export default GuestDetails;